// Interactive builder configurations — systemic method-based model.
// Positive-only pricing: base print cost + garment sourcing + upgrades.
// Compute builders use exact owner price tables; "derived" marks provisional deltas.

import {
  Shirt, CupSoda, Layers, CreditCard, Frame, Footprints, ShoppingBag, Printer, Package,
  PartyPopper, GraduationCap, Rocket, Tag, Cookie, Candy, Droplets, GlassWater,
  Signpost, Fan,
} from "lucide-react";
import { money } from "@/lib/api";

// ——— Apparel: method-specific model (master update) ———

const GARMENT_DELTA = { customer: 0, poly: 800, cotton: 1000 };

const SUBLIMATION_WARNING =
  "Garment must be suitable for sublimation — preferably light-coloured with high-polyester content.";

const sourcingStep = (method) => ({
  id: "sourcing",
  label: "Garment Sourcing",
  dynamicOptions: () => {
    const m = method || "dtf";
    const opts = [
      {
        id: "customer", label: "Customer Supplies Own Garment", delta: 0,
        desc: m === "sublimation" ? `JMD $0 garment surcharge. ${SUBLIMATION_WARNING}` : "JMD $0 garment surcharge.",
      },
      { id: "poly", label: "TLF Designz Supplies Polyester Shirt", delta: 800, desc: "+JMD $800 per garment." },
    ];
    if (m !== "sublimation") {
      opts.push({ id: "cotton", label: "TLF Designz Supplies Cotton Shirt", delta: 1000, desc: "+JMD $1,000 per garment." });
    }
    return opts;
  },
});

// Method step remains only on products that genuinely offer multiple methods.
const METHOD_STEP = {
  id: "method",
  label: "Base Print Method (Print Only / Customer Provides Garment)",
  options: [
    { id: "htv", label: "HTV / Vinyl Printing Only", base: 1500 }, // sheet
    { id: "sublimation", label: "Sublimation Printing Only", base: 1800 }, // sheet
    { id: "dtf", label: "DTF (Direct to Film) Printing Only", base: 2200 }, // sheet
  ],
};

const METHOD_SOURCING_STEP = {
  id: "sourcing",
  label: "Garment Sourcing",
  dynamicOptions: (sel) => sourcingStep(sel.method === "sublimation" ? "sublimation" : "dtf").dynamicOptions(sel),
};

const PLACEMENT_STEP = {
  id: "placement",
  label: "Print Placement Upgrades",
  options: [
    { id: "standard", label: "Standard Single Placement (Front OR Back)", delta: 0 }, // sheet
    { id: "dual", label: "Dual Placement (Front AND Back)", delta: 1350 }, // sheet
  ],
};

// ——— Apparel pricing engine (compute-based; exact owner tables) ———
// DTF size tiers: Standard 8.5"×11" / Large 11"×17" / Oversized 13"×19" ("Full Coverage" is sublimation-only).
const DTF_PLACEMENTS = [
  { id: "sleeve", label: "Sleeve", price: 700 },
  { id: "left-chest", label: "Left Chest", price: 800 },
  { id: "standard", label: "Standard Front OR Back", price: 1500, desc: 'Up to 8.5" × 11"' },
  { id: "large", label: "Large Front OR Back", price: 2500, desc: 'Up to 11" × 17"' },
  { id: "oversized", label: "Oversized Front OR Back", price: 3000, desc: 'Up to 13" × 19"' },
  { id: "standard-fb", label: "Standard Front + Back", price: 2800, desc: 'Up to 8.5" × 11" on each side' },
  { id: "large-fb", label: "Large Front + Back", price: 4500, desc: 'Up to 11" × 17" on each side' },
  { id: "oversized-fb", label: "Oversized Front + Back", price: 5500, desc: 'Up to 13" × 19" on each side' },
];

// Sublimation coverage tiers — "Full Coverage – Complete Shirt" = front, back AND sleeves.
const SUB_PLACEMENTS = [
  { id: "standard", label: "Standard Print", price: 1200, desc: 'Up to 8.5" × 11"' },
  { id: "large", label: "Large Print", price: 1600, desc: 'Up to 11" × 17"' },
  { id: "xl", label: "Extra Large Print", price: 2200, desc: 'Up to 13" × 19" — suits larger garments such as XL, 2XL and 3XL' },
  { id: "full-front", label: "Full Coverage – Front Only", price: 2800 },
  { id: "full-complete", label: "Full Coverage – Complete Shirt", price: 3300, desc: "Complete sublimation coverage including front, back and sleeves." },
];

// HTV / Vinyl — placements + locked specialty-finish matrix. No Large/Oversized tiers.
// Name & Number lives on the Jersey builder, not regular HTV/Vinyl.
const HTV_PLACEMENTS = [
  { id: "sleeve", label: "Sleeve" },
  { id: "left-chest", label: "Left Chest" },
  { id: "standard", label: "Standard Front OR Back" },
  { id: "front-back", label: "Front + Back" },
];

const HTV_FINISHES = [
  { id: "standard", label: "Standard" },
  { id: "glitter", label: "Glitter" },
  { id: "holographic", label: "Holographic" },
  { id: "reflective", label: "Reflective" },
  { id: "puff", label: "Puff" },
  { id: "flock", label: "Flock / Velvet" },
];

const HTV_PRICES = {
  standard: { sleeve: 600, "left-chest": 700, standard: 1200, "front-back": 2200 },
  glitter: { sleeve: 700, "left-chest": 800, standard: 1600, "front-back": 2600 },
  holographic: { sleeve: 700, "left-chest": 800, standard: 1600, "front-back": 2600 },
  reflective: { sleeve: 700, "left-chest": 800, standard: 1600, "front-back": 2700 },
  puff: { sleeve: 700, "left-chest": 800, standard: 1600, "front-back": 2700 },
  flock: { sleeve: 700, "left-chest": 800, standard: 1600, "front-back": 2600 },
};

const placementStep = (list, label) => ({
  id: "placement",
  label,
  options: list.map((p) => ({ id: p.id, label: p.label, delta: 0, desc: p.desc })),
});

const HTV_FINISH_STEP = {
  id: "finish",
  label: "Vinyl Finish",
  options: HTV_FINISHES.map((f) => ({ id: f.id, label: f.label, delta: 0 })),
};

// ——— Actual garment sizes (S–3XL); surcharge only when TLF supplies the shirt ———
const ACTUAL_SIZES = ["s", "m", "l", "xl", "2xl", "3xl"];

const sizeStep = (xlFee) => ({
  id: "gsize",
  label: "Garment Size",
  dynamicOptions: (sel) =>
    ACTUAL_SIZES.map((s) => ({
      id: s,
      label: s.toUpperCase(),
      delta: 0,
      desc:
        sel.sourcing !== "customer" && (s === "2xl" || s === "3xl")
          ? `+${money(xlFee)} per garment`
          : undefined,
    })),
});

// Per-garment sizes — only when TLF supplies AND more than one garment.
const mixedSizesField = (xlFee) => ({
  id: "sizes-note",
  label: "Sizes For Each Garment",
  visibleIf: (sel, qty) => sel.sourcing !== "customer" && qty > 1,
  fields: [
    {
      id: "sizes-mixed",
      label: "Sizes For Each Garment (optional override)",
      type: "textarea",
      wide: true,
      placeholder: "One size per garment, e.g. S, M, M, 2XL",
      help: `When filled, this list overrides the size dropdown. 2XL/3XL add +${money(xlFee)} per affected garment — surcharges are calculated from the sizes listed here.`,
    },
  ],
});

const countBigSizes = (text) =>
  (text || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t === "2xl" || t === "3xl").length;

const SOURCING_LABELS = {
  customer: "Customer Supplies Own Garment",
  poly: "TLF Supplies Polyester Shirt",
  cotton: "TLF Supplies Cotton Shirt",
};

const sizePart = (sel, q) => {
  const mixed = (sel["sizes-mixed"] || "").trim();
  if (mixed && q > 1 && sel.sourcing !== "customer") return `Sizes: ${mixed}`;
  return `Size ${(sel.gsize || "m").toUpperCase()}`;
};

// Shared apparel math: (print + garment) × qty + size surcharges. Bulk discounts apply to the print portion only.
const apparelMath = (sel, qty, printUnit, xlFee) => {
  const q = Math.max(1, qty || 1);
  const garment = sel.sourcing === "customer" ? 0 : GARMENT_DELTA[sel.sourcing] || 0;
  const mixed = (sel["sizes-mixed"] || "").trim();
  const bigCount =
    sel.sourcing !== "customer"
      ? mixed && q > 1
        ? countBigSizes(mixed)
        : sel.gsize === "2xl" || sel.gsize === "3xl"
          ? q
          : 0
      : 0;
  const sizeSur = bigCount * xlFee;
  const total = (printUnit + garment) * q + sizeSur;
  return { q, garment, sizeSur, total, unit: total / q, discountable: printUnit * q };
};

// Multi-method products (Birthday & Memorial, Jerseys) keep their method bases + placement upgrade.
const multiMethodCompute = (xlFee) => (sel, qty) => {
  const methodBase = { htv: 1500, sublimation: 1800, dtf: 2200 }[sel.method] || 1500;
  const printUnit = methodBase + (sel.placement === "dual" ? 1350 : 0);
  const m = apparelMath(sel, qty, printUnit, xlFee);
  const methodLabel = { htv: "HTV / Vinyl", sublimation: "Sublimation", dtf: "DTF" }[sel.method] || "HTV / Vinyl";
  return {
    total: m.total,
    unit: m.unit,
    units: m.q,
    discountable: m.discountable,
    parts: [
      `${methodLabel} Print`,
      sel.placement === "dual" ? "Dual Placement (Front + Back)" : "Standard Single Placement",
      SOURCING_LABELS[sel.sourcing] || SOURCING_LABELS.customer,
      sizePart(sel, m.q),
    ],
  };
};

// ——— Jersey customization types (Name & Number belongs here, not regular HTV) ———
// Name/number work is covered by the method base; Full Jersey Design carries the
// established full-coverage upgrade (+JMD $1,350, same as dual-placement full coverage).
const JERSEY_CUSTOM_STEP = {
  id: "jcustom",
  label: "Jersey Customization",
  options: [
    { id: "name-only", label: "Name Only", delta: 0 },
    { id: "number-only", label: "Number Only", delta: 0 },
    { id: "name-number", label: "Name & Number", delta: 0 },
    { id: "full-design", label: "Full Jersey Design", delta: 1350, desc: "Full-coverage custom design across the jersey." },
  ],
};
const JERSEY_CUSTOM_LABELS = {
  "name-only": "Name Only",
  "number-only": "Number Only",
  "name-number": "Name & Number",
  "full-design": "Full Jersey Design",
};

// ——— Drinkware dropdowns ———
const WRAP = {
  id: "wrap",
  label: "Design / Wrap Style",
  options: [
    { id: "basic", label: "Basic Design", delta: 0 },
    { id: "full-wrap", label: "Full Wrap Design", delta: 500 }, // sheet deltas
  ],
};

const WRAP_40OZ = {
  id: "wrap",
  label: "Design / Wrap Style",
  options: [
    { id: "basic", label: "Basic Design", delta: 0 }, // sheet 7000
    { id: "half-wrap-upper", label: "Half Wrap – Upper Section", delta: 500 }, // derived
    { id: "full-wrap", label: "Full Wrap – All Printable Areas", delta: 1000 }, // sheet 8000
  ],
};

const SG_SIZE = {
  id: "size",
  label: "Base Tumbler Size",
  options: [
    { id: "16oz", label: "16 oz Base", delta: 0 },
    { id: "24oz", label: "24 oz Base", delta: 1500 }, // sheet
  ],
};

const SG_GLITTER = {
  id: "glitter",
  label: "Glitter Flow Solution",
  options: [
    { id: "regular", label: "Regular Glitter Flow", delta: 0 },
    { id: "slow-fall", label: "Slow-Fall Glitter", delta: 300 }, // sheet
    { id: "pearl", label: "Pearl Shimmer Effect", delta: 300 }, // sheet
    { id: "lava", label: "Lava Lamp Effect", delta: 500 }, // sheet
  ],
};

const SG_STICKER = {
  id: "sticker",
  label: "Sticker Decoration Add-On",
  options: [
    { id: "none", label: "Base / No Sticker Add-On", delta: 0 },
    { id: "name-only", label: "Name Only", delta: 300 }, // sheet
    { id: "name-small", label: "Name + Small Sticker Design", delta: 500 }, // sheet
    { id: "full", label: "Full Sticker Design", delta: 800 }, // sheet
  ],
};

// ——— Personalization fields (shared) ———

const DESIGN_HELP =
  "Describe the wording, colours, theme, design idea, names, dates, placement preferences or any other details you would like us to use.";

const PERSONALIZATION_FIELDS = {
  id: "details",
  label: "Personalization Details",
  fields: [
    { id: "custom_name", label: "Name / Text", placeholder: "e.g. Aaliyah, Team Sharks" },
    { id: "theme", label: "Colour / Theme", placeholder: "e.g. Cyan & black, tropical" },
    {
      id: "design", label: "Design Details / What would you like?", type: "textarea", wide: true,
      placeholder: "Tell us what you want — no artwork needed",
      help: DESIGN_HELP,
    },
    { id: "instructions", label: "Special Instructions", type: "textarea", wide: true, placeholder: "Placement, sizing, notes…" },
  ],
};

// ——— Stickers & Labels (final sheet pricing matrix, 1–20 sheets) ———

const DIECUT_NOTE =
  "Die-cut size is based on the longest side of the finished sticker. Unusually wide or inefficient artwork may require a quantity adjustment after artwork review.";

const STICKER_GROUPS = [
  {
    heading: "2-Inch",
    shapes: [
      { id: "2-square", label: "Square", size: "2-inch", perSheet: 20 },
      { id: "2-circle", label: "Circle", size: "2-inch", perSheet: 20 },
      { id: "2-diecut", label: "Die-Cut", size: "2-inch", perSheet: 20, note: DIECUT_NOTE },
      { id: "2-rect-2x1", label: 'Rectangle — 2"×1"', size: "2-inch", perSheet: 20 },
    ],
  },
  {
    heading: "3-Inch",
    shapes: [
      { id: "3-square", label: "Square", size: "3-inch", perSheet: 8 },
      { id: "3-circle", label: "Circle", size: "3-inch", perSheet: 8 },
      { id: "3-diecut", label: "Die-Cut", size: "3-inch", perSheet: 8, note: DIECUT_NOTE },
      { id: "3-rect-3x1", label: 'Rectangle — 3"×1"', size: "3-inch", perSheet: 20 },
      { id: "3-rect-3x2", label: 'Rectangle — 3"×2"', size: "3-inch", perSheet: 10 },
    ],
  },
  {
    heading: "4-Inch",
    shapes: [
      { id: "4-square", label: "Square", size: "4-inch", perSheet: 6 },
      { id: "4-circle", label: "Circle", size: "4-inch", perSheet: 6 },
      { id: "4-diecut", label: "Die-Cut", size: "4-inch", perSheet: 6, note: DIECUT_NOTE },
      { id: "4-rect-4x1", label: 'Rectangle — 4"×1"', size: "4-inch", perSheet: 20 },
      { id: "4-rect-4x2", label: 'Rectangle — 4"×2"', size: "4-inch", perSheet: 8 },
      { id: "4-rect-4x3", label: 'Rectangle — 4"×3"', size: "4-inch", perSheet: 6 },
    ],
  },
];

const STICKER_SHAPES = STICKER_GROUPS.flatMap((g) => g.shapes);
const stickerShape = (id) => STICKER_SHAPES.find((s) => s.id === id) || STICKER_SHAPES[0];

const STICKER_TYPES = [
  {
    id: "economy-paper", label: "Economy Matte Stickers",
    desc: "Affordable matte paper sticker with easy-peel backing. Best for indoor branding, packaging seals and temporary labels.",
  },
  {
    id: "premium-glossy", label: "Premium Glossy Crack-and-Peel Stickers",
    desc: "Premium glossy paper sticker with crack-and-peel backing. Vibrant colour and a shiny finish for standout branding, packaging and labels.",
  },
  {
    id: "white", label: "White Vinyl Sticker",
    desc: "White adhesive vinyl with strong colour reproduction. Suitable for product labels, branding, packaging and general-purpose stickers.",
  },
  {
    id: "clear", label: "Clear Vinyl Sticker",
    desc: "Transparent adhesive vinyl for a clean, no-background appearance. Best on glass, white and light-coloured surfaces.",
  },
];

const CLEAR_NOTICE =
  "IMPORTANT: TLF Designz does not print white ink. White areas in your artwork will appear transparent. Pale colours may appear less vibrant on dark surfaces.";

const VINYL_FINISHES = [
  {
    id: "unlaminated", label: "Unlaminated",
    desc: "Durable adhesive sticker with a clean printed finish. Suitable for general labels and everyday use. Water resistance depends on material and application.",
  },
  {
    id: "laminated", label: "Laminated",
    desc: "Added protective laminate for improved water, scratch and wear resistance. Recommended when extra durability is needed.",
  },
];

// Finish choices depend on Sticker Type — incompatible combos are impossible.
const STICKER_FINISHES = {
  "economy-paper": [
    {
      id: "crack-peel", label: "Crack & Peel",
      desc: "Best for indoor branding, packaging seals and temporary labels. Matte paper finish. Not waterproof.",
    },
  ],
  "premium-glossy": [
    {
      id: "crack-peel", label: "Crack & Peel",
      desc: "Glossy paper finish with easy crack-and-peel backing. Vibrant shine. Not waterproof.",
    },
  ],
  white: VINYL_FINISHES,
  clear: VINYL_FINISHES,
};

// Final owner anchor TOTALS (JMD) per material — these are total order prices, not per-sheet
// rates. In-between sheet quantities interpolate linearly between the surrounding anchors and
// round to the nearest J$50; anchor quantities always return their exact listed total.
const STICKER_ANCHORS = {
  "economy-paper": { 1: 650, 5: 2650, 10: 4750, 20: 8500, 50: 18500 },
  "premium-glossy": { 1: 850, 5: 3500, 10: 6250, 20: 11000, 50: 24000 },
  "white-unlaminated": { 1: 1100, 5: 4500, 10: 8000, 20: 14500, 50: 32000 },
  "clear-unlaminated": { 1: 1200, 5: 4900, 10: 8750, 20: 16000, 50: 35000 },
};
const LAMINATION_PER_SHEET = 300;

const round50 = (v) => Math.round(v / 50) * 50;

const stickerBase = (type, n) => {
  const anchors = STICKER_ANCHORS[type];
  if (anchors[n] !== undefined) return anchors[n];
  const keys = Object.keys(anchors).map(Number).sort((a, b) => a - b);
  const upper = keys.find((k) => k > n);
  const lower = [...keys].reverse().find((k) => k < n);
  const t = (n - lower) / (upper - lower);
  return round50(anchors[lower] + t * (anchors[upper] - anchors[lower]));
};

const stickerTypeLabel = (id) => (STICKER_TYPES.find((t) => t.id === id) || STICKER_TYPES[0]).label;
const stickerFinishLabel = (type, id) =>
  (STICKER_FINISHES[type] || STICKER_FINISHES.white).find((f) => f.id === id)?.label ||
  STICKER_FINISHES[type]?.[0]?.label || "";

// ——— Business cards & flyers tables (exact owner matrices) ———

// [qty]["finish-sides"] — matte is the lower-cost finish; glossy prices unchanged.
const CARD_PRICES = {
  50: { "matte-single": 1700, "matte-double": 2200, "glossy-single": 2500, "glossy-double": 3000 },
  100: { "matte-single": 2800, "matte-double": 3600, "glossy-single": 3800, "glossy-double": 4700 },
  200: { "matte-single": 5200, "matte-double": 6400, "glossy-single": 6800, "glossy-double": 8300 },
};

// [size][qty]["paper-sides"] — exact lookups, no per-50 surcharges.
const FLYER_PRICES = {
  "4x5": {
    50: { "200-single": 2500, "200-double": 3500, "260-single": 3000, "260-double": 4000 },
    100: { "200-single": 4500, "200-double": 6000, "260-single": 5500, "260-double": 7000 },
    250: { "200-single": 9500, "200-double": 13000, "260-single": 11500, "260-double": 15500 },
    500: { "200-single": 17500, "200-double": 24000, "260-single": 21000, "260-double": 28500 },
    1000: { "200-single": 30000, "200-double": 45000, "260-single": 37000, "260-double": 55000 },
  },
  "5x7": {
    50: { "200-single": 5000, "200-double": 6500, "260-single": 6000, "260-double": 7500 },
    100: { "200-single": 8500, "200-double": 11000, "260-single": 10000, "260-double": 13000 },
    250: { "200-single": 18500, "200-double": 25000, "260-single": 22000, "260-double": 29000 },
    500: { "200-single": 33000, "200-double": 45000, "260-single": 40000, "260-double": 52000 },
    1000: { "200-single": 58000, "200-double": 82000, "260-single": 72000, "260-double": 95000 },
  },
};

const flyerTurnaround = (u) => `⏱️ Production Time: ${u <= 100 ? "1 - 2" : u <= 250 ? "2 - 3" : u <= 500 ? "3 - 4" : "4 - 5"} Business Days`;

// ——— Buttons tier pricing (per-unit by quantity) ———

const BUTTON_TIERS = {
  standard: [[24, 150], [49, 130], [74, 110], [99, 95], [100, 80]],
  holographic: [[24, 200], [49, 180], [74, 160], [99, 145], [100, 130]],
};
const tierUnit = (tiers, qty) => (tiers.find(([max]) => qty <= max) || tiers[tiers.length - 1])[1];

// ——— Party / events tier pricing (per-unit by quantity) ———

const PARTY_FIELDS = {
  id: "details",
  label: "Personalization Details",
  fields: [
    { id: "custom_name", label: "Name / Text", placeholder: "e.g. Aaliyah's 10th" },
    { id: "theme", label: "Theme / Colours", placeholder: "e.g. Frozen, cyan & pink" },
    {
      id: "design", label: "Design Details / What would you like?", type: "textarea", wide: true,
      placeholder: "Tell us what you want — no artwork needed",
      help: DESIGN_HELP,
    },
    { id: "instructions", label: "Special Instructions", type: "textarea", wide: true, placeholder: "Anything we should know…" },
  ],
};

// ——— Birthday & Memorial apparel fields (text-first; artwork optional) ———
const TRIBUTE_FIELDS = {
  id: "details",
  label: "Shirt Details",
  fields: [
    { id: "custom_name", label: "Name (Birthday Person / Loved One)", placeholder: "Who is this shirt for?" },
    { id: "age", label: "Age (optional)", placeholder: "Birthday shirts only" },
    { id: "dob", label: "Date of Birth (optional)", placeholder: "Memorial shirts only" },
    { id: "dod", label: "Date of Passing (optional)", placeholder: "Memorial shirts only" },
    { id: "wording", label: "Wording / Quote / Tribute Text", type: "textarea", wide: true, placeholder: "e.g. 'Forever in our hearts' or 'Level 10 Unlocked'" },
    { id: "theme", label: "Theme / Colours", placeholder: "e.g. Purple & gold, roses" },
    {
      id: "design", label: "Design Details / What would you like?", type: "textarea", wide: true,
      placeholder: "Describe the design — wording-only orders are welcome",
      help: DESIGN_HELP,
    },
    { id: "instructions", label: "Special Instructions", type: "textarea", wide: true, placeholder: "Anything else we should know…" },
  ],
};

// ——— Custom Jersey fields (sports / teams; player name & number optional) ———
const JERSEY_FIELDS = {
  id: "details",
  label: "Jersey Design Details",
  fields: [
    { id: "sport", label: "Sport / Team Type", placeholder: "e.g. Football, netball, track & field" },
    { id: "team", label: "Team Name (optional)", placeholder: "e.g. Harbour View FC" },
    { id: "player_name", label: "Player Name (optional)", placeholder: "Leave blank if not needed" },
    { id: "player_number", label: "Player Number (optional)", placeholder: "Leave blank if not needed" },
    { id: "theme", label: "Colours / Theme", placeholder: "e.g. Home kit — cyan & black" },
    {
      id: "design", label: "Design Details / What would you like?", type: "textarea", wide: true,
      placeholder: "Describe the design — wording-only orders are welcome",
      help: DESIGN_HELP,
    },
    {
      id: "roster", label: "Team Orders: Names / Numbers / Sizes (optional)", type: "textarea", wide: true,
      placeholder: "One jersey per line, e.g. S — 'TJ' #7",
      help: "For multi-jersey orders, list each jersey's size, name and number here.",
    },
    { id: "instructions", label: "Special Instructions", type: "textarea", wide: true, placeholder: "Logo placement, references, deadlines…" },
  ],
};

const tieredCompute = (tiers, qtyMin, labelFn) => (sel, qty) => {
  const q = Math.max(qtyMin, qty || qtyMin);
  const unit = tierUnit(tiers, q);
  return { total: unit * q, unit, units: q, parts: labelFn(sel, q, unit) };
};

const apparelTurnaround = (q) =>
  `⏱️ Production Time: ${q <= 5 ? "1 - 2" : q <= 10 ? "2 - 3" : q <= 24 ? "3 - 4" : q <= 49 ? "4 - 5" : q <= 99 ? "5 - 7" : "7 - 10"} Business Days${q >= 100 ? " (Confirmation Required)" : ""}`;

const apparelRushTarget = (q) =>
  q <= 5
    ? "⚡ RUSH READY: Next Business Day (where feasible)"
    : q <= 10
      ? "⚡ RUSH READY: 1 - 2 Business Days"
      : "⚡ RUSH READY: 2 - 3 Business Days";

// ——— Merch packs (full per-recipient package prices — already discounted, no bulk stacking) ———

const MERCH_PACKS = [
  {
    id: "staff-essentials", label: "Staff Essentials Pack", price: 5500,
    desc: "Per recipient: 1 Branded DTF T-Shirt, 1 Branded 11 oz Mug, 1 Acrylic Keychain.",
  },
  {
    id: "premium-staff", label: "Premium Staff Pack", price: 8500,
    desc: "Per recipient: 1 Branded DTF T-Shirt, 1 16 oz Tumbler, 1 Mouse Pad, 1 Acrylic Keychain.",
  },
  {
    id: "client-appreciation", label: "Client Appreciation Pack", price: 5400,
    desc: "Per recipient: 1 12 oz Branded Wine Tumbler, 1 Sublimation Tote Bag, 1 Acrylic Keychain.",
  },
  {
    id: "event-promo-crew", label: "Event / Promo Crew Pack", price: 4500,
    desc: "Per recipient: 1 Branded DTF T-Shirt, 1 Personalized Drawstring Bag, 1 Acrylic Keychain.",
  },
];

// ——— Extra shirt add-on steps (packages) ———

const EXTRA_SHIRT_STEPS = (label) => [
  {
    id: "extra-shirts",
    label,
    select: true,
    options: [0, 1, 2, 3, 4, 5].map((n) => ({
      id: String(n),
      label: n === 0 ? "Not Added" : `+${n} Shirt${n > 1 ? "s" : ""} — +${money(2300 * n)}`,
      delta: 0,
    })),
  },
  {
    id: "extra-shirt-material",
    label: "Extra Shirt Material",
    visibleIf: (sel) => Number(sel["extra-shirts"]) > 0,
    options: [
      { id: "cotton", label: "Cotton", delta: 0 },
      { id: "poly", label: "Polyester", delta: 0 },
    ],
  },
  {
    id: "extra-shirt-size",
    label: "Extra Shirt Size",
    visibleIf: (sel) => Number(sel["extra-shirts"]) > 0,
    options: ACTUAL_SIZES.map((s) => ({
      id: s,
      label: s.toUpperCase(),
      delta: 0,
      desc: s === "2xl" || s === "3xl" ? "+JMD $500 per affected shirt" : undefined,
    })),
  },
  {
    id: "extra-shirt-specs-wrap",
    label: "Per-Shirt Sizes & Materials",
    visibleIf: (sel) => Number(sel["extra-shirts"]) > 1,
    fields: [
      {
        id: "extra-shirt-specs",
        label: "Size & material for each extra shirt (optional)",
        type: "textarea",
        wide: true,
        placeholder: "One per line, e.g. M Cotton / 2XL Polyester",
        help: "When filled, this list overrides the dropdowns above. 2XL/3XL add +JMD $500 per affected shirt.",
      },
    ],
  },
];

const extraShirtCost = (sel) => {
  const n = Number(sel["extra-shirts"]) || 0;
  if (!n) return 0;
  const specs = (sel["extra-shirt-specs"] || "").trim();
  const big =
    specs && n > 1
      ? countBigSizes(specs)
      : sel["extra-shirt-size"] === "2xl" || sel["extra-shirt-size"] === "3xl"
        ? n
        : 0;
  return n * 2300 + big * 500;
};

const extraShirtPart = (sel) => {
  const n = Number(sel["extra-shirts"]) || 0;
  if (!n) return [];
  const specs = (sel["extra-shirt-specs"] || "").trim();
  const mat = sel["extra-shirt-material"] === "poly" ? "Polyester" : "Cotton";
  if (specs && n > 1) return [`+${n} Extra Shirt${n > 1 ? "s" : ""} (${specs.replace(/\n/g, "; ")})`];
  return [`+${n} Extra Shirt${n > 1 ? "s" : ""} (${mat}, Size ${(sel["extra-shirt-size"] || "m").toUpperCase()})`];
};

// ——— Birthday add-ons (fixed bundle pricing — already bulk-discounted, never double-discounted) ———
const BIRTHDAY_ADDON_BUNDLES = [
  { id: "addon-ring-pop", label: "Ring Pop Wraps", prices: { 10: 2400, 25: 5800, 50: 10600, 100: 20000 } },
  { id: "addon-pringles", label: "Pringles Small Tin Wraps", prices: { 10: 2900, 25: 7000, 50: 12750, 100: 24000 } },
  { id: "addon-choc-sneakers", label: "Chocolate Sneakers Wraps", prices: { 10: 2400, 25: 5800, 50: 10600, 100: 20000 } },
  { id: "addon-gable", label: "Personalized Gable Boxes", prices: { 10: 5800, 25: 13950, 50: 25500, 100: 48000 } },
];

const bundleAddonStep = (a) => ({
  id: a.id,
  label: a.label,
  select: true,
  options: [
    { id: "0", label: "Not Added", delta: 0 },
    ...Object.entries(a.prices).map(([n, p]) => ({ id: n, label: `${n} pcs — ${money(p)}`, delta: 0 })),
  ],
});

const BIRTHDAY_ADDON_STEPS = [
  {
    id: "sticker-upgrade",
    label: '3" Sticker Sheet Upgrade',
    options: [
      { id: "2in", label: '2-inch Sticker Sheets (Included)', delta: 0 },
      {
        id: "3in", label: 'Upgrade to 3-inch Sheets — No Extra Charge', delta: 0,
        desc: "Replaces the included 2-inch sheets; estimated sticker quantity recalculates at 8 per sheet.",
      },
    ],
  },
  ...BIRTHDAY_ADDON_BUNDLES.map(bundleAddonStep),
  {
    id: "addon-sign",
    label: '18" × 24" Foam-Board Welcome Sign',
    select: true,
    options: [0, 1, 2].map((n) => ({
      id: String(n),
      label: n === 0 ? "Not Added" : `+${n} Sign${n > 1 ? "s" : ""} — +${money(5500 * n)}`,
      delta: 0,
    })),
  },
];

const birthdayAddonCost = (sel) =>
  BIRTHDAY_ADDON_BUNDLES.reduce((sum, a) => sum + (a.prices[Number(sel[a.id])] || 0), 0) +
  (Number(sel["addon-sign"]) || 0) * 5500;

const birthdayAddonParts = (sel) => {
  const parts = [];
  if (sel["sticker-upgrade"] === "3in") parts.push('3-inch Sticker Sheet Upgrade (8 per sheet)');
  for (const a of BIRTHDAY_ADDON_BUNDLES) {
    const n = Number(sel[a.id]) || 0;
    if (n > 0) parts.push(`${a.label} × ${n} pcs`);
  }
  const signs = Number(sel["addon-sign"]) || 0;
  if (signs > 0) parts.push(`Welcome Sign × ${signs}`);
  return parts;
};

// ——— Graduation add-ons (approved prices; quantity dropdowns multiply the line total) ———
const GRAD_ADDONS = [
  { id: "addon-fans", label: "Extra Graduation Fans", price: 500, max: 10 },
  { id: "addon-buttons", label: "Extra Custom Buttons", price: 150, max: 10 },
  { id: "addon-sheets", label: 'Extra 2-inch Sticker Sheets', price: 1300, max: 5 },
  { id: "addon-keychain", label: "Acrylic Keychain", price: 800, max: 10 },
  { id: "addon-tumbler", label: "Personalized 16oz Tumbler", price: 4000, max: 3 },
  { id: "addon-slate", label: 'Personalized 10" Aluminum Photo Slate', price: 6000, max: 3 },
  { id: "addon-frame", label: 'Personalized 5"×7" LED Acrylic Photo Frame', price: 5000, max: 3 },
  { id: "addon-sign", label: '18" × 24" Foam-Board Welcome Sign', price: 5500, max: 2 },
];

const qtyAddonStep = (a) => ({
  id: a.id,
  label: a.label,
  select: true,
  options: Array.from({ length: a.max + 1 }, (_, i) => ({
    id: String(i),
    label: i === 0 ? "Not Added" : `+${i} — +${money(a.price * i)}`,
    delta: 0,
  })),
});

const GRAD_ADDON_STEPS = GRAD_ADDONS.map(qtyAddonStep);

const gradAddonCost = (sel) =>
  GRAD_ADDONS.reduce((sum, a) => sum + (Number(sel[a.id]) || 0) * a.price, 0);

const gradAddonParts = (sel) =>
  GRAD_ADDONS.filter((a) => Number(sel[a.id]) > 0).map((a) => `${a.label} × ${sel[a.id]}`);

// Included package shirt — material & size are informational (no surcharge on included shirts).
const INCLUDED_SHIRT_STEPS = [
  {
    id: "shirt-material",
    label: "Included Shirt Material",
    options: [
      { id: "cotton", label: "Cotton", delta: 0 },
      { id: "poly", label: "Polyester", delta: 0 },
    ],
  },
  {
    id: "shirt-size",
    label: "Included Shirt Size",
    options: ACTUAL_SIZES.map((s) => ({ id: s, label: s.toUpperCase(), delta: 0 })),
  },
];

// ——— Birthday packages (fixed tiers, full package prices) ———

const BIRTHDAY_PACKS = [
  {
    id: "essentials", label: "Birthday Essentials Pack", price: 7700, versioned: false,
    desc: "1 Birthday Shirt, 12 Branded Plastic Party Cups, 12 Custom Chip Bags, 1 Sheet of 2-inch Stickers (20 stickers).",
  },
  {
    id: "party", label: "Birthday Party Pack", price: 26700, versioned: true, versionDelta: 600,
    desc: "1 Birthday Shirt, 24 Branded Plastic Party Cups, 24 Custom Chip Bags, 24 Birthday Fans, 24 Capri Sun Labels OR 24 Cool Fruit Juice Box Wraps, 2 Sheets of 2-inch Stickers (40 stickers).",
  },
  {
    id: "premium", label: "Premium Birthday Celebration Pack", price: 46700, versioned: true, versionDelta: 900,
    desc: "1 Birthday Shirt, 1 Personalized Celebrant Tumbler/Cup, 36 Party Cups, 36 Chip Bags, 36 Birthday Fans, 36 Capri Sun Labels OR 36 Cool Fruit Wraps, 2 Sheets of 2-inch Stickers (40 stickers), 1 Matching 18×24 Foam-Board Welcome Sign.",
  },
  {
    id: "ultimate", label: "Ultimate Birthday Celebration Pack", price: 56200, versioned: true, versionDelta: 1000,
    desc: "1 Birthday Shirt, 1 Personalized Celebrant Tumbler/Cup, 50 Party Cups, 50 Chip Bags, 50 Birthday Fans, 50 Capri Sun Labels OR 50 Cool Fruit Wraps, 3 Sheets of 2-inch Stickers (60 stickers), 1 Matching 18×24 Foam-Board Welcome Sign.",
  },
];

const BIRTHDAY_FIELDS = {
  id: "details",
  label: "Party Details",
  fields: [
    { id: "custom_name", label: "Birthday Person's Name", placeholder: "Who are we celebrating?" },
    { id: "age", label: "Age", placeholder: "e.g. 10" },
    { id: "theme", label: "Theme / Colours", placeholder: "e.g. Spider-Man, cyan & yellow" },
    { id: "event_date", label: "Event Date", placeholder: "e.g. 2026-08-14" },
    { id: "instructions", label: "Special Instructions", placeholder: "Anything we should know…" },
  ],
};

// ——— Graduation packages (personalized keepsakes, fixed tiers) ———

const GRAD_PACKS = [
  {
    id: "essentials", label: "Graduation Essentials Pack", price: 6700,
    desc: "1 Graduation Shirt, 2 Graduation Fans, 2 Custom Buttons, 1 Sheet of 2-inch Graduation Stickers (20 stickers), 1 Personalized Journal.",
  },
  {
    id: "celebration", label: "Graduation Celebration Pack", price: 15100,
    desc: "3 Graduation Shirts, 5 Graduation Fans, 4 Custom Buttons, 1 Sheet of 2-inch Graduation Stickers (20 stickers), 1 Personalized Journal.",
  },
  {
    id: "premium", label: "Premium Graduation Pack", price: 23800,
    desc: "4 Graduation Shirts, 6 Graduation Fans, 6 Custom Buttons, 2 Sheets of 2-inch Graduation Stickers (40 stickers), 1 Personalized Tumbler, 1 Personalized Journal, 1 Personalized Gift Bag.",
  },
];

const GRAD_FIELDS = {
  id: "details",
  label: "Graduate Details",
  fields: [
    { id: "custom_name", label: "Graduate's Name", placeholder: "Who are we celebrating?" },
    { id: "theme", label: "School / Theme / Colours", placeholder: "e.g. KC, purple & gold" },
    { id: "event_date", label: "Event Date", placeholder: "e.g. 2026-07-03" },
    { id: "instructions", label: "Special Instructions", placeholder: "Anything we should know…" },
  ],
};

// ——— Business Launch Pack upgrades (price differences from product matrices) ———

const LAUNCH_BASE = 15500;
const LAUNCH_CONTENTS =
  "Includes: 100 matte single-sided business cards, 100 × 4\" × 5\" 200-GSM single-sided flyers, 60 × 2-inch white unlaminated stickers, and 2 Standard Front DTF cotton shirts. Individual component value JMD $16,800 — package price JMD $15,500.";

export const BUILDER_CONFIGS = {
  // ——— Custom Apparel (method-specific; method fixed internally) ———
  "dtf-custom-t-shirt": {
    title: "DTF Custom T-Shirt", subtitle: "Direct-to-Film • Custom Apparel",
    accent: "cyan", icon: Shirt, categorySlug: "custom-apparel", garment: true,
    defaults: { method: "dtf", gsize: "m" },
    dropdowns: [
      placementStep(DTF_PLACEMENTS, "Print Placement & Size"),
      sourcingStep("dtf"),
      sizeStep(500),
      mixedSizesField(500),
      PERSONALIZATION_FIELDS,
    ],
    compute: (sel, qty) => {
      const p = DTF_PLACEMENTS.find((x) => x.id === sel.placement) || DTF_PLACEMENTS[2];
      const m = apparelMath(sel, qty, p.price, 500);
      return {
        total: m.total, unit: m.unit, units: m.q, discountable: m.discountable,
        parts: [p.label, SOURCING_LABELS[sel.sourcing] || SOURCING_LABELS.customer, sizePart(sel, m.q)],
      };
    },
  },
  "sublimation-custom-t-shirt": {
    title: "Sublimation Custom T-Shirt", subtitle: "Dye Sublimation • Custom Apparel",
    accent: "cyan", icon: Shirt, categorySlug: "custom-apparel", garment: true,
    defaults: { method: "sublimation", gsize: "m" },
    dropdowns: [
      placementStep(SUB_PLACEMENTS, "Print Size & Coverage"),
      sourcingStep("sublimation"),
      sizeStep(300),
      mixedSizesField(300),
      PERSONALIZATION_FIELDS,
    ],
    compute: (sel, qty) => {
      const p = SUB_PLACEMENTS.find((x) => x.id === sel.placement) || SUB_PLACEMENTS[0];
      const m = apparelMath(sel, qty, p.price, 300);
      return {
        total: m.total, unit: m.unit, units: m.q, discountable: m.discountable,
        parts: [p.label, SOURCING_LABELS[sel.sourcing] || SOURCING_LABELS.customer, sizePart(sel, m.q)],
      };
    },
  },
  "birthday-memorial-shirt": {
    title: "Birthday & Memorial Shirt", subtitle: "Tribute Prints • Custom Apparel",
    accent: "cyan", icon: Shirt, categorySlug: "custom-apparel", garment: true,
    defaults: { gsize: "m" },
    dropdowns: [METHOD_STEP, METHOD_SOURCING_STEP, PLACEMENT_STEP, sizeStep(500), mixedSizesField(500), TRIBUTE_FIELDS],
    compute: multiMethodCompute(500),
  },
  "htv-vinyl-custom-t-shirt": {
    title: "HTV / Vinyl Custom T-Shirt", subtitle: "Heat-Transfer Vinyl • Custom Apparel",
    accent: "cyan", icon: Shirt, categorySlug: "custom-apparel", garment: true,
    defaults: { method: "htv", gsize: "m", finish: "standard" },
    dropdowns: [
      placementStep(HTV_PLACEMENTS, "Print Placement"),
      HTV_FINISH_STEP,
      sourcingStep("htv"),
      sizeStep(500),
      mixedSizesField(500),
      PERSONALIZATION_FIELDS,
    ],
    compute: (sel, qty) => {
      const placement = HTV_PLACEMENTS.some((p) => p.id === sel.placement) ? sel.placement : "standard";
      const finish = HTV_PRICES[sel.finish] ? sel.finish : "standard";
      const print = HTV_PRICES[finish][placement] ?? HTV_PRICES.standard[placement];
      const m = apparelMath(sel, qty, print, 500);
      const pLabel = HTV_PLACEMENTS.find((p) => p.id === placement).label;
      const fLabel = HTV_FINISHES.find((f) => f.id === finish).label;
      return {
        total: m.total, unit: m.unit, units: m.q, discountable: m.discountable,
        parts: [pLabel, `${fLabel} Vinyl`, SOURCING_LABELS[sel.sourcing] || SOURCING_LABELS.customer, sizePart(sel, m.q)],
      };
    },
  },
  "custom-jersey": {
    title: "Custom Jersey", subtitle: "Sports & Team Wear • Custom Apparel",
    accent: "cyan", icon: Shirt, categorySlug: "custom-apparel", garment: true,
    defaults: { method: "htv", gsize: "m" },
    dropdowns: [METHOD_STEP, METHOD_SOURCING_STEP, JERSEY_CUSTOM_STEP, sizeStep(500), mixedSizesField(500), JERSEY_FIELDS],
    compute: (sel, qty) => {
      const methodBase = { htv: 1500, sublimation: 1800, dtf: 2200 }[sel.method] || 1500;
      const printUnit = methodBase + (sel.jcustom === "full-design" ? 1350 : 0);
      const m = apparelMath(sel, qty, printUnit, 500);
      const methodLabel = { htv: "HTV / Vinyl", sublimation: "Sublimation", dtf: "DTF" }[sel.method] || "HTV / Vinyl";
      return {
        total: m.total,
        unit: m.unit,
        units: m.q,
        discountable: m.discountable,
        parts: [
          `${methodLabel} Print`,
          JERSEY_CUSTOM_LABELS[sel.jcustom] || "Name & Number",
          SOURCING_LABELS[sel.sourcing] || SOURCING_LABELS.customer,
          sizePart(sel, m.q),
        ],
      };
    },
  },
  // ——— Custom Drinkware ———
  "11oz-custom-mug": {
    title: "Personalized 11oz Mug", subtitle: "Ceramic • Custom Drinkware",
    accent: "magenta", icon: CupSoda, categorySlug: "custom-drinkware", base: 2000,
    dropdowns: [WRAP, PERSONALIZATION_FIELDS],
  },
  "12oz-wine-tumbler": {
    title: "Personalized 12oz Wine Tumbler", subtitle: "Insulated Stemless • Custom Drinkware",
    accent: "magenta", icon: CupSoda, categorySlug: "custom-drinkware", base: 3500,
    dropdowns: [WRAP, PERSONALIZATION_FIELDS],
  },
  "16oz-sublimation-tumbler": {
    title: "Personalized 16oz Sublimation Tumbler", subtitle: "Double-Wall Stainless • Custom Drinkware",
    accent: "magenta", icon: CupSoda, categorySlug: "custom-drinkware", base: 4000,
    dropdowns: [WRAP, PERSONALIZATION_FIELDS],
  },
  "kids-sublimation-cup": {
    title: "Kids Sublimation Cup", subtitle: "Kid-Proof • Custom Drinkware",
    accent: "magenta", icon: CupSoda, categorySlug: "custom-drinkware", base: 4000,
    dropdowns: [WRAP, PERSONALIZATION_FIELDS],
  },
  "40oz-sublimation-tumbler": {
    title: "40oz Sublimation Tumbler", subtitle: "Big Handle • Custom Drinkware",
    accent: "magenta", icon: CupSoda, categorySlug: "custom-drinkware", base: 7000,
    dropdowns: [WRAP_40OZ, PERSONALIZATION_FIELDS],
  },
  "snow-globe-tumbler": {
    title: "Personalized Snow Globe Tumbler", subtitle: "Glitter Double-Wall • Custom Drinkware",
    accent: "magenta", icon: CupSoda, categorySlug: "custom-drinkware", base: 5000,
    dropdowns: [SG_SIZE, SG_GLITTER, SG_STICKER, PERSONALIZATION_FIELDS],
  },
  // ——— Business Printing ———
  "custom-stickers-labels": {
    title: "Custom Stickers & Labels", subtitle: "Sheet Production • Business Printing",
    accent: "magenta", icon: Layers, categorySlug: "business-printing",
    noQty: true, oversized: true,
    dropdowns: [
      {
        id: "shape",
        label: "Shape / Size",
        groups: STICKER_GROUPS.map((g) => ({
          heading: g.heading,
          options: g.shapes.map((s) => ({ id: s.id, label: s.label, delta: 0, note: s.note })),
        })),
      },
      {
        id: "sheets",
        label: "Sheet Quantity",
        select: true,
        quoteOption: true,
        options: Array.from({ length: 50 }, (_, i) => {
          const n = i + 1;
          return { id: String(n), label: `${n} Sheet${n > 1 ? "s" : ""}`, delta: 0 };
        }),
        helper: (sel) => {
          const shape = stickerShape(sel.shape);
          const sheets = Number(sel.sheets) || 1;
          return `📦 Yield: You will receive ${sheets * shape.perSheet} total stickers (${shape.perSheet} per sheet).`;
        },
      },
      {
        id: "type",
        label: "Sticker Type",
        options: STICKER_TYPES.map((t) => ({ id: t.id, label: t.label, delta: 0, desc: t.desc })),
        helper: (sel) => (sel.type === "clear" ? `⚠️ ${CLEAR_NOTICE}` : null),
      },
      {
        id: "finish",
        label: "Finish",
        dynamicOptions: (sel) =>
          (STICKER_FINISHES[sel.type] || STICKER_FINISHES.white).map((f) => ({
            id: f.id, label: f.label, delta: 0, desc: f.desc,
          })),
      },
    ],
    quoteRows: (sel, pricing) => {
      const shape = stickerShape(sel.shape);
      const sheets = Number(sel.sheets) || 1;
      const type = STICKER_FINISHES[sel.type] ? sel.type : "white";
      return [
        ["Shape", shape.label],
        ["Size", shape.size],
        ["Sticker Type", stickerTypeLabel(sel.type)],
        ["Finish", stickerFinishLabel(type, sel.finish)],
        ["Number of Sheets", `${sheets}`],
        ["Stickers Per Sheet", `${shape.perSheet}`],
        ["Total Sticker Quantity", `${pricing.units}`],
        ["Unit Price Per Sticker", money(pricing.unit)],
      ];
    },
    compute: (sel) => {
      const shape = stickerShape(sel.shape);
      const sheets = Number(sel.sheets) || 1;
      const type = STICKER_FINISHES[sel.type] ? sel.type : "white";
      const finish = (STICKER_FINISHES[type].some((f) => f.id === sel.finish)
        ? sel.finish
        : STICKER_FINISHES[type][0].id);
      let total;
      if (type === "economy-paper" || type === "premium-glossy") {
        total = stickerBase(type, sheets);
      } else {
        total = stickerBase(type === "clear" ? "clear-unlaminated" : "white-unlaminated", sheets)
          + (finish === "laminated" ? LAMINATION_PER_SHEET * sheets : 0);
      }
      const units = sheets * shape.perSheet;
      return {
        total,
        unit: total / units,
        units,
        parts: [
          `${shape.label} — ${shape.size}`,
          `${sheets} Sheet${sheets > 1 ? "s" : ""}`,
          stickerTypeLabel(type),
          stickerFinishLabel(type, finish),
        ],
      };
    },
  },
  "custom-business-cards": {
    title: "Custom Business Cards", subtitle: "Heavyweight Stock • Business Printing",
    accent: "magenta", icon: CreditCard, categorySlug: "business-printing",
    noQty: true,
    dropdowns: [
      {
        id: "qty",
        label: "Quantity",
        select: true,
        options: [50, 100, 200].map((q) => ({ id: String(q), label: `${q} Cards`, delta: 0 })),
      },
      {
        id: "method",
        label: "Sides",
        options: [
          { id: "single", label: "Single-Sided", delta: 0 },
          { id: "double", label: "Double-Sided", delta: 0 },
        ],
      },
      {
        id: "finish",
        label: "Finish",
        options: [
          { id: "matte", label: "Matte", delta: 0, desc: "Soft, non-reflective finish — the lower-cost option." },
          { id: "glossy", label: "Glossy", delta: 0, desc: "High-shine, vibrant finish." },
        ],
      },
    ],
    compute: (sel) => {
      const q = Number(sel.qty) || 50;
      const finish = sel.finish === "glossy" ? "glossy" : "matte";
      const sides = sel.method === "double" ? "double" : "single";
      const total = CARD_PRICES[q][`${finish}-${sides}`];
      return {
        total,
        unit: total / q,
        units: q,
        parts: [
          `${q} Cards`,
          sides === "double" ? "Double-Sided" : "Single-Sided",
          finish === "glossy" ? "Glossy" : "Matte",
        ],
      };
    },
  },
  "flyers-50": {
    title: "Custom Flyers", subtitle: "Marketing Runs • Business Printing",
    accent: "magenta", icon: Printer, categorySlug: "business-printing",
    noQty: true,
    notice: "Flyers are PRINT ONLY — please supply completed, print-ready artwork. No design fee is added.",
    rush: {
      standard: flyerTurnaround,
      rush: (u) =>
        u <= 100
          ? "⚡ RUSH: Same-day may be available when fully paid with approved print-ready artwork by 11:00 AM — otherwise next business day"
          : u <= 250
            ? "⚡ RUSH READY: 1 - 2 Business Days"
            : "⚡ Rush availability must be confirmed with TLF Designz for this quantity",
      pct: 0.25, min: 1000, confirmAt: 500,
    },
    dropdowns: [
      {
        id: "size",
        label: "Size",
        options: [
          { id: "4x5", label: '4" × 5"', delta: 0 },
          { id: "5x7", label: '5" × 7"', delta: 0 },
        ],
      },
      {
        id: "paper",
        label: "Paper",
        options: [
          { id: "200gsm", label: "200 GSM — Standard", delta: 0 },
          { id: "260gsm", label: "260 GSM — Premium", delta: 0 },
        ],
      },
      {
        id: "sides",
        label: "Sides",
        options: [
          { id: "single", label: "Single-Sided", delta: 0 },
          { id: "double", label: "Double-Sided", delta: 0 },
        ],
      },
      {
        id: "qty",
        label: "Quantity",
        select: true,
        options: [50, 100, 250, 500, 1000].map((q) => ({ id: String(q), label: `${q} Flyers`, delta: 0 })),
      },
    ],
    compute: (sel) => {
      const q = Number(sel.qty) || 50;
      const size = FLYER_PRICES[sel.size] ? sel.size : "4x5";
      const paper = sel.paper === "260gsm" ? "260" : "200";
      const sides = sel.sides === "double" ? "double" : "single";
      const total = FLYER_PRICES[size][q][`${paper}-${sides}`];
      return {
        total,
        unit: total / q,
        units: q,
        parts: [
          size === "5x7" ? '5" × 7"' : '4" × 5"',
          paper === "260" ? "260 GSM Premium" : "200 GSM Standard",
          sides === "double" ? "Double-Sided" : "Single-Sided",
          `${q} Qty`,
        ],
      };
    },
  },
  "company-branded-merch-pack": {
    title: "Company Merch Packs", subtitle: "Purpose-Driven Business Packs • Business Printing",
    accent: "magenta", icon: Package, categorySlug: "business-printing",
    qtyLabel: "Number of Recipients",
    notice: "Full package prices per recipient — already discounted. For 101+ recipients, please request a custom quote.",
    dropdowns: [
      {
        id: "pack",
        label: "Merch Pack",
        options: MERCH_PACKS.map((p) => ({
          id: p.id, label: `${p.label} — ${money(p.price)}`, delta: 0, desc: p.desc,
        })),
      },
      {
        id: "details",
        label: "Branding Details",
        fields: [
          { id: "custom_name", label: "Business Name", placeholder: "Your company name" },
          { id: "theme", label: "Brand Colours / Theme", placeholder: "e.g. Navy & gold" },
          { id: "instructions", label: "Special Instructions", placeholder: "Sizes, notes…" },
        ],
      },
    ],
    compute: (sel, qty) => {
      const pack = MERCH_PACKS.find((p) => p.id === sel.pack) || MERCH_PACKS[0];
      const q = Math.max(1, qty || 1);
      return {
        total: pack.price * q,
        unit: pack.price,
        units: q,
        parts: [pack.label, `${q} Recipient${q > 1 ? "s" : ""}`],
      };
    },
  },
  "business-launch-pack": {
    title: "Business Launch Pack", subtitle: "One-Package Business Starter • Business Printing",
    accent: "magenta", icon: Rocket, categorySlug: "business-printing",
    noQty: true,
    notice: `${LAUNCH_CONTENTS} One business package per order — for larger or custom launch packages, please request a quote.`,
    dropdowns: [
      {
        id: "cards",
        label: "Business Cards Upgrade",
        options: [
          { id: "matte-single", label: "Matte Single-Sided (Included)", delta: 0 },
          { id: "glossy-single", label: "Glossy Single-Sided", delta: 200 },
          { id: "matte-double", label: "Double-Sided", delta: 800 },
          { id: "glossy-double", label: "Glossy + Double-Sided", delta: 1000 },
        ],
      },
      {
        id: "flyer-paper",
        label: "Flyer Paper Upgrade",
        options: [
          { id: "200", label: "200 GSM (Included)", delta: 0 },
          { id: "260", label: "260 GSM Premium", delta: 800 },
        ],
      },
      {
        id: "flyer-sides",
        label: "Flyer Sides Upgrade",
        options: [
          { id: "single", label: "Single-Sided (Included)", delta: 0 },
          { id: "double", label: "Double-Sided", delta: 1500 },
        ],
      },
      {
        id: "stickers",
        label: "Sticker Upgrade",
        options: [
          { id: "white-unlam", label: "White Unlaminated (Included)", delta: 0 },
          { id: "clear-unlam", label: "Clear Stickers", delta: 600, desc: "+JMD $200 × 3 sheets." },
          { id: "white-lam", label: "Laminated Stickers", delta: 900, desc: "+JMD $300 × 3 sheets." },
          { id: "clear-lam", label: "Clear + Laminated", delta: 1500, desc: "+JMD $500 × 3 sheets." },
        ],
      },
      {
        id: "shirts",
        label: "Extra Standard DTF Shirts",
        select: true,
        options: [0, 1, 2, 3, 4, 5].map((n) => ({
          id: String(n),
          label: n === 0 ? "No Extra Shirts" : `+${n} Shirt${n > 1 ? "s" : ""} — +${money(2300 * n)}`,
          delta: 0,
        })),
      },
      {
        id: "shirt-size",
        label: "Extra Shirt Sizes",
        dynamicOptions: (sel) =>
          Number(sel.shirts) > 0
            ? [
                { id: "std", label: "S – XL (No surcharge)", delta: 0 },
                { id: "xl-plus", label: "2XL / 3XL", delta: 500, desc: "+JMD $500 per extra shirt." },
              ]
            : [{ id: "std", label: "S – XL", delta: 0 }],
      },
    ],
    compute: (sel) => {
      const extraShirts = Number(sel.shirts) || 0;
      const extra =
        ({ "glossy-single": 200, "matte-double": 800, "glossy-double": 1000 }[sel.cards] || 0) +
        (sel["flyer-paper"] === "260" ? 800 : 0) +
        (sel["flyer-sides"] === "double" ? 1500 : 0) +
        ({ "clear-unlam": 600, "white-lam": 900, "clear-lam": 1500 }[sel.stickers] || 0) +
        extraShirts * 2300 +
        (sel["shirt-size"] === "xl-plus" ? extraShirts * 500 : 0);
      const total = LAUNCH_BASE + extra;
      const parts = ["Base Launch Package"];
      if (sel.cards && sel.cards !== "matte-single") parts.push("Card Upgrade");
      if (sel["flyer-paper"] === "260") parts.push("260 GSM Flyers");
      if (sel["flyer-sides"] === "double") parts.push("Double-Sided Flyers");
      if (sel.stickers && sel.stickers !== "white-unlam") parts.push("Sticker Upgrade");
      if (extraShirts > 0)
        parts.push(`+${extraShirts} Extra Shirt${extraShirts > 1 ? "s" : ""}${sel["shirt-size"] === "xl-plus" ? " (2XL/3XL)" : ""}`);
      return { total, unit: total, units: 1, parts };
    },
  },
  // ——— Photo & Gift Items ———
  "led-acrylic-photo-frame": {
    title: "Personalized LED Acrylic Photo Frame", subtitle: "Edge-Lit • Photo & Gift Items",
    accent: "cyan", icon: Frame, categorySlug: "photo-gift-items", base: 5000,
    dropdowns: [
      {
        id: "size",
        label: "Frame Size",
        options: [
          { id: "5x7", label: '5" × 7" Size', delta: 0 }, // sheet
          { id: "6x8", label: '6" × 8" Size', delta: 1000 }, // sheet
        ],
      },
      PERSONALIZATION_FIELDS,
    ],
  },
  "aluminum-photo-slate-10-round": {
    title: "Personalized Aluminum Photo Slate / Print", subtitle: "Gloss Aluminum • Photo & Gift Items",
    accent: "cyan", icon: Frame, categorySlug: "photo-gift-items", base: 6000,
    dropdowns: [
      {
        id: "format",
        label: "Format",
        options: [
          { id: "10-round", label: '10" Round Photo Slate', delta: 0 }, // sheet
          { id: "11x14", label: '11" × 14" Aluminum Photo Print', delta: 3000 }, // sheet
        ],
      },
      PERSONALIZATION_FIELDS,
    ],
  },
  "aluminum-photo-print-11x14": {
    title: "Personalized Aluminum Photo Slate / Print", subtitle: "Gloss Aluminum • Photo & Gift Items",
    accent: "cyan", icon: Frame, categorySlug: "photo-gift-items", base: 6000,
    defaults: { format: "11x14" },
    dropdowns: [
      {
        id: "format",
        label: "Format",
        options: [
          { id: "10-round", label: '10" Round Photo Slate', delta: 0 },
          { id: "11x14", label: '11" × 14" Aluminum Photo Print', delta: 3000 },
        ],
      },
      PERSONALIZATION_FIELDS,
    ],
  },
  "custom-fridge-magnet": {
    title: "Custom Fridge Magnet", subtitle: "Sealed Gloss • Photo & Gift Items",
    accent: "cyan", icon: Frame, categorySlug: "photo-gift-items", base: 500,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "custom-mouse-pad": {
    title: "Custom Mouse Pad", subtitle: "Stitched Edge • Photo & Gift Items",
    accent: "cyan", icon: Frame, categorySlug: "photo-gift-items", base: 1500,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "rhinestone-photo-necklace": {
    title: "Personalized Rhinestone Photo Necklace", subtitle: "Photo Jewelry • Photo & Gift Items",
    accent: "cyan", icon: Frame, categorySlug: "photo-gift-items", base: 3500,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "custom-225-buttons": {
    title: "Custom 2.25-inch Buttons", subtitle: "Pin-Back • Print-Only • Photo & Gift Items",
    accent: "cyan", icon: Tag, categorySlug: "photo-gift-items",
    dropdowns: [
      {
        id: "finish",
        label: "Finish",
        options: [
          { id: "standard", label: "Standard", delta: 0, desc: "From JMD $80–$150 each by quantity." },
          { id: "holographic", label: "Holographic", delta: 0, desc: "From JMD $130–$200 each by quantity." },
        ],
      },
      PERSONALIZATION_FIELDS,
    ],
    compute: (sel, qty) => {
      const q = Math.max(1, qty || 1);
      const finish = sel.finish === "holographic" ? "holographic" : "standard";
      const unit = tierUnit(BUTTON_TIERS[finish], q);
      return {
        total: unit * q,
        unit,
        units: q,
        parts: [`${q} Buttons`, finish === "holographic" ? "Holographic" : "Standard"],
      };
    },
  },
  // ——— Bags & Accessories ———
  "sublimation-tote-bag": {
    title: '13" × 15" Sublimation Tote Bag', subtitle: "Full-Color Sublimation • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 1500,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "drawstring-backpack": {
    title: "Personalized Drawstring Bag", subtitle: "Event Cinch Pack • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 1000,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "cosmetic-pencil-pouch": {
    title: "Cosmetic / Pencil Pouch", subtitle: "Sublimation • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 700,
    dropdowns: [
      {
        id: "coverage",
        label: "Print Coverage",
        options: [
          { id: "1-side", label: "1 Side", delta: 0 }, // sheet
          { id: "2-side", label: "2 Sides", delta: 200 }, // sheet
        ],
      },
      PERSONALIZATION_FIELDS,
    ],
  },
  "custom-structured-hat": {
    title: "Custom Structured Hat", subtitle: "Vinyl / Patch Front • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 2500,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "acrylic-sublimation-keychain": {
    title: "Personalized Acrylic Sublimation Keychain", subtitle: "Double-Sided • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 800,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "custom-bottle-opener": {
    title: "Custom Bottle Opener", subtitle: "Full-Color Branded Face • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 1000,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "car-coaster-set": {
    title: "Personalized Car Coaster Set", subtitle: "Set of 2 • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 1000,
    qtyLabel: "Number of Sets",
    notice: "1 quantity = one set of 2 coasters.",
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  "car-window-sign": {
    title: "Personalized Car Window Sign", subtitle: "Suction-Mount • Bags & Accessories",
    accent: "yellow", icon: ShoppingBag, categorySlug: "custom-bags-accessories", base: 1500,
    dropdowns: [PERSONALIZATION_FIELDS],
  },
  // ——— Events & Occasions: packages ———
  "birthday-party-bundle": {
    title: "Birthday Party Packages", subtitle: "Fixed Celebration Tiers • Events & Occasions",
    accent: "yellow", icon: PartyPopper, categorySlug: "events-occasions",
    noQty: true,
    notice: "Fixed package pricing — full package price shown, no hidden upgrades. Optional add-ons below are priced separately. Buttons and other party add-ons are available separately too.",
    dropdowns: [
      {
        id: "pack",
        label: "Package Tier",
        options: BIRTHDAY_PACKS.map((p) => ({
          id: p.id,
          label: p.versioned
            ? `${p.label} — from ${money(p.price)}`
            : `${p.label} — ${money(p.price)}`,
          delta: 0,
          desc: p.desc,
        })),
      },
      {
        id: "version",
        label: "Juice Label Version",
        dynamicOptions: (sel) => {
          const pack = BIRTHDAY_PACKS.find((p) => p.id === sel.pack) || BIRTHDAY_PACKS[0];
          if (!pack.versioned) return [{ id: "none", label: "Not included in this package", delta: 0 }];
          return [
            { id: "capri", label: `Capri Sun Labels — ${money(pack.price)}`, delta: 0 },
            { id: "cool-fruit", label: `Cool Fruit Juice Box Wraps — ${money(pack.price + pack.versionDelta)}`, delta: pack.versionDelta },
          ];
        },
      },
      ...INCLUDED_SHIRT_STEPS,
      BIRTHDAY_FIELDS,
      ...EXTRA_SHIRT_STEPS("Extra Standard DTF Shirts"),
      ...BIRTHDAY_ADDON_STEPS,
    ],
    compute: (sel) => {
      const pack = BIRTHDAY_PACKS.find((p) => p.id === sel.pack) || BIRTHDAY_PACKS[0];
      const versionUp = pack.versioned && sel.version === "cool-fruit" ? pack.versionDelta : 0;
      const total = pack.price + versionUp + extraShirtCost(sel) + birthdayAddonCost(sel);
      return {
        total,
        unit: total,
        units: 1,
        parts: [
          pack.label,
          `Shirt: ${sel["shirt-material"] === "poly" ? "Polyester" : "Cotton"}, Size ${(sel["shirt-size"] || "m").toUpperCase()}`,
          ...(pack.versioned ? [sel.version === "cool-fruit" ? "Cool Fruit Juice Box Wraps" : "Capri Sun Labels"] : []),
          ...extraShirtPart(sel),
          ...birthdayAddonParts(sel),
        ],
      };
    },
  },
  "graduation-packages": {
    title: "Graduation Packages", subtitle: "Personalized Keepsake Tiers • Events & Occasions",
    accent: "yellow", icon: GraduationCap, categorySlug: "events-occasions",
    noQty: true,
    notice: "Fixed package pricing — full package price shown. Optional add-ons (tumblers, journals, signs, labels and more) are available separately.",
    dropdowns: [
      {
        id: "pack",
        label: "Package Tier",
        options: GRAD_PACKS.map((p) => ({
          id: p.id, label: `${p.label} — ${money(p.price)}`, delta: 0, desc: p.desc,
        })),
      },
      GRAD_FIELDS,
      ...EXTRA_SHIRT_STEPS("Extra Graduation Shirts"),
      ...GRAD_ADDON_STEPS,
    ],
    compute: (sel) => {
      const pack = GRAD_PACKS.find((p) => p.id === sel.pack) || GRAD_PACKS[0];
      const total = pack.price + extraShirtCost(sel) + gradAddonCost(sel);
      return {
        total,
        unit: total,
        units: 1,
        parts: [pack.label, ...extraShirtPart(sel), ...gradAddonParts(sel)],
      };
    },
  },
  // ——— Events & Occasions: party add-ons (tiered per-unit) ———
  "branded-party-cups": {
    title: "Branded Plastic Party Cups", subtitle: "Printed Sticker-Label Branding • Party Add-Ons",
    accent: "yellow", icon: CupSoda, categorySlug: "events-occasions",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[49, 150], [99, 140], [199, 125], [299, 115], [9999, 105]], 1,
      (sel, q, u) => [`${q} Party Cups`, `${money(u)} each`]),
  },
  "custom-chip-bags": {
    title: "Custom Chip / Snack Bags", subtitle: "Full Custom Wrapper • Party Add-Ons",
    accent: "yellow", icon: Cookie, categorySlug: "events-occasions",
    notice: "Working production format: 8.5 × 11 glossy 120–150 GSM wrapper — 1 wrapper per sheet.",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[19, 250], [49, 225], [99, 200], [199, 175], [9999, 160]], 1,
      (sel, q, u) => [`${q} Chip Bags`, `${money(u)} each`]),
  },
  "graduation-fans-10pk": {
    title: "Custom Big-Head Fans", subtitle: "Party & Graduation Volume Rates • Party Add-Ons",
    accent: "yellow", icon: Fan, categorySlug: "events-occasions",
    qtyMin: 12,
    notice: "Minimum order of 12 fans. Volume rate applies automatically by quantity.",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[23, 500], [49, 450], [99, 400], [199, 350], [9999, 325]], 12,
      (sel, q, u) => [`${q} Fans`, `${money(u)} each`]),
  },
  "branded-water-labels": {
    title: "Branded Water Bottle Labels (iCool 500 ml)", subtitle: "8 × 2 in Template • Party Add-Ons",
    accent: "yellow", icon: Droplets, categorySlug: "events-occasions",
    qtyMin: 12,
    notice: "Template: 8 × 2 inches — approx. 5 per 8.5 × 11 sticker sheet. Price covers the custom printed label; bottled water can be supplied separately.",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[23, 175], [49, 160], [99, 145], [199, 130], [9999, 120]], 12,
      (sel, q, u) => [`${q} Water Labels`, `${money(u)} each`]),
  },
  "capri-sun-labels": {
    title: "Capri Sun Labels", subtitle: "3.5 × 5 in Template • Party Add-Ons",
    accent: "yellow", icon: GlassWater, categorySlug: "events-occasions",
    qtyMin: 12,
    notice: "Template: 3.5 × 5 inches — approx. 4 per sheet. Price covers the custom printed label.",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[23, 200], [49, 185], [99, 170], [199, 150], [9999, 140]], 12,
      (sel, q, u) => [`${q} Capri Sun Labels`, `${money(u)} each`]),
  },
  "cool-fruit-wraps": {
    title: "Cool Fruit Juice Box Wraps (200 ml)", subtitle: "Custom Wrap Template • Party Add-Ons",
    accent: "yellow", icon: GlassWater, categorySlug: "events-occasions",
    qtyMin: 12,
    notice: "Working template approx. 6.5 × 4.5 inches — approx. 2 per sheet. Verified against a physical Cool Fruit box before bulk production. Price covers the custom printed wrap.",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[23, 225], [49, 210], [99, 190], [199, 170], [9999, 155]], 12,
      (sel, q, u) => [`${q} Juice Box Wraps`, `${money(u)} each`]),
  },
  "mini-chocolate-wrappers": {
    title: "Mini Chocolate Wrappers", subtitle: "Approx. 3.15 × 1.5 in • Party Add-Ons",
    accent: "yellow", icon: Candy, categorySlug: "events-occasions",
    qtyMin: 12,
    notice: "Template approx. 3.15 × 1.5 inches — approx. 15 per sheet. Price covers the custom printed wrapper; chocolate can be supplied separately.",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[23, 100], [49, 90], [99, 80], [199, 70], [9999, 60]], 12,
      (sel, q, u) => [`${q} Mini Wrappers`, `${money(u)} each`]),
  },
  "hershey-bar-wrappers": {
    title: "Regular Hershey Bar Wrappers", subtitle: "Approx. 5.5 × 5.25 in • Party Add-Ons",
    accent: "yellow", icon: Candy, categorySlug: "events-occasions",
    qtyMin: 12,
    notice: "Template approx. 5.5 × 5.25 inches — approx. 2 per sheet. Price covers the custom printed wrapper; chocolate can be supplied separately.",
    dropdowns: [PARTY_FIELDS],
    compute: tieredCompute([[23, 225], [49, 210], [99, 190], [199, 170], [9999, 155]], 12,
      (sel, q, u) => [`${q} Hershey Wrappers`, `${money(u)} each`]),
  },
  "foam-board-welcome-sign": {
    title: '18" × 24" Custom Foam-Board Welcome Sign', subtitle: "Print + Mount + Finish • Party Add-Ons",
    accent: "yellow", icon: Signpost, categorySlug: "events-occasions",
    notice: "Includes full-colour customization, printing, mounting, and trimming/finishing.",
    dropdowns: [PARTY_FIELDS],
    compute: (sel, qty) => {
      const q = Math.max(1, qty || 1);
      return { total: 5500 * q, unit: 5500, units: q, parts: [`${q} × 18"×24" Foam-Board Sign${q > 1 ? "s" : ""}`] };
    },
  },
  // ——— Sneakers (customer supplies base sneaker) ———
  "custom-sneakers-basic-art": {
    title: "Customized Sneakers", subtitle: "Customer Supplies the Base Sneaker",
    accent: "cyan", icon: Footprints, categorySlug: "customized-sneakers", base: 3000,
    alwaysDropOff: true,
    dropdowns: [
      {
        id: "tier",
        label: "Customization Tier (Customer Supplies the Base Sneaker)",
        options: [
          {
            id: "basic", label: "Basic Customization", delta: 0,
            desc: "Covers simple name, initials, or clean wording + a basic graphic asset. Customization is applied strictly to 1 panel of the shoe.",
          },
          {
            id: "standard", label: "Standard Customization", delta: 1500,
            desc: "Includes customization across 2–3 distinct panels with name/text, multi-color graphics, simple patterns, solid background blocks, or custom fabric inserts.",
          },
          {
            id: "premium", label: "Premium Customization", delta: 3500,
            desc: "Heavy, intricate artistic detailing across several areas of the shoe. Includes custom glitter work, layered design effects, multi-material blends, and highly detailed custom graphic artwork layouts.",
          },
          {
            id: "full", label: "Full Customization", delta: 5500,
            desc: "Artwork covering most or all customizable areas of the shoe. Full cohesive theme or concept, multiple special effects, complex materials, detailed graphics, and substantially more design preparation and application time.",
          },
        ],
      },
      PERSONALIZATION_FIELDS,
    ],
  },
  "sneaker-refresh-repaint": {
    title: "Sneaker Refresh & Repaint", subtitle: "Assessment First • Customer Supplies Sneakers",
    accent: "cyan", icon: Footprints, categorySlug: "customized-sneakers", base: 4500,
    alwaysDropOff: true,
    notice: "Submit clear photos of your sneakers and their current condition — every refresh is assessed before the job is confirmed.",
    dropdowns: [
      {
        id: "details",
        label: "Condition & Instructions",
        fields: [
          { id: "condition", label: "Shoe Condition Notes", placeholder: "Scuffs, yellowing, cracking…" },
          { id: "instructions", label: "Special Instructions", placeholder: "Colours, finish, references…" },
        ],
      },
    ],
  },
};

export const LEGACY_BUILDERS = {
  "stickers-labels": "custom-stickers-labels",
  "snow-globe-tumblers": "snow-globe-tumbler",
};

export const STANDARD_QTY = [1, 2, 5, 10, 25, 50, 100];

// ——— Production timelines & rush order upgrades ———
// Site-wide rush: +25% of the applicable order subtotal, minimum JMD $1,000.
// Apparel & flyers carry qty-scaled targets; sneakers are approval-only (no automatic charge).
export const RUSH_TIERS = {
  // Stickers & Business Cards
  "business-printing": {
    standard: "⏱️ Production Time: 2 - 3 Business Days",
    rush: "⚡ RUSH READY: Next-Day Turnaround (24 Hours)",
    pct: 0.25, min: 1000,
  },
  // Custom Apparel
  "custom-apparel": {
    standard: apparelTurnaround,
    rush: apparelRushTarget,
    pct: 0.25, min: 1000, confirmAt: 25,
  },
  "customized-sneakers": {
    standard: "⏱️ Production Time: 5 - 7 Business Days",
    approvalOnly: "⚡ Rush service subject to approval. Contact TLF Designz to confirm availability.",
  },
  "events-occasions": {
    standard: "⏱️ Production Time: 5 - 7 Business Days",
    rush: "⚡ RUSH READY: 2 - 3 Business Days",
    pct: 0.25, min: 1000,
  },
  // Drinkware & Photo Gifts
  "custom-drinkware": {
    standard: "⏱️ Production Time: 1 - 2 Business Days",
    rush: "⚡ RUSH READY: Same-Day / Next-Day Turnaround",
    pct: 0.25, min: 1000,
  },
  "photo-gift-items": {
    standard: "⏱️ Production Time: 1 - 2 Business Days",
    rush: "⚡ RUSH READY: Same-Day / Next-Day Turnaround",
    pct: 0.25, min: 1000,
  },
  "custom-bags-accessories": {
    standard: "⏱️ Production Time: 1 - 2 Business Days",
    rush: "⚡ RUSH READY: Same-Day / Next-Day Turnaround",
    pct: 0.25, min: 1000,
  },
};
